import { useState, type ReactNode } from 'react';
import { Outlet, useNavigate, useRouterState } from '@tanstack/react-router';
import {
  AppShell, Avatar, Banner, Button, Dialog, List, ListItem, NavRail, NavRailItem, SegmentedButton, Select,
  TextField, Tooltip,
} from '@meshble/molo';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api, must, mustOk } from './lib/api';
import {
  Archive, Assistente, Blueprint as IconaBlueprint, Database, Globe, History, Group, Key, More, Pacchetto, Server, Shield, Trasloco, Tune,
} from './icons';
import { Login, NoOrganizations, isUnauthenticated, useMe } from './session';
import { Anello, Logo, Wordmark } from './brand';
import { impostaLingua, lingua, linguaDelBrowser, localeIntl, t, type Lingua } from './lib/lingua';

type Sezione = 'servers' | 'instances' | 'catalogo' | 'blueprint' | 'backup' | 'continuita' | 'traslochi' | 'ip' | 'jobs' | 'members' | 'registro' | 'assistenti' | 'impostazioni' | 'accessi';

/**
 * Le destinazioni, nell'ordine della rail. Sul telefono la barra in fondo ha
 * posto per cinque voci: ci stanno le quattro che si aprono fuori ufficio —
 * come stanno i server, un'istanza da riavviare, un lavoro da seguire, il
 * giorno brutto — e «Altro» con le secondarie.
 *
 * `permesso` è quello che serve per aprirla, o uno qualunque se sono più
 * d'uno: senza, la voce non compare. Server, istanze e attività si vedono
 * sempre, perché i loro elenchi mostrano a ciascuno ciò che gli spetta,
 * anche a un cliente che ha solo accessi sulle risorse. `staff` è
 * solo per lo staff dell'hub.
 */
const VOCI: Array<{ sezione: Sezione; label: string; icona: ReactNode; to: string; secondaria?: boolean; permesso?: string | string[]; staff?: boolean }> = [
  { sezione: 'servers', label: 'Server', icona: <Server />, to: '/orgs/$slug/servers' },
  { sezione: 'instances', label: 'Istanze', icona: <Database />, to: '/orgs/$slug/instances' },
  { sezione: 'catalogo', label: 'Catalogo', icona: <Pacchetto />, to: '/orgs/$slug/catalogo', permesso: 'instance.deploy', secondaria: true },
  { sezione: 'blueprint', label: 'Blueprint', icona: <IconaBlueprint />, to: '/orgs/$slug/blueprint', permesso: 'blueprint.manage', secondaria: true },
  { sezione: 'backup', label: 'Backup', icona: <Archive />, to: '/orgs/$slug/backup', permesso: 'backup.read', secondaria: true },
  { sezione: 'continuita', label: 'Continuità', icona: <Shield />, to: '/orgs/$slug/continuita', permesso: 'backup.read' },
  { sezione: 'traslochi', label: 'Traslochi', icona: <Trasloco />, to: '/orgs/$slug/traslochi', permesso: 'migration.manage', secondaria: true },
  { sezione: 'ip', label: 'Indirizzi IP', icona: <Globe />, to: '/orgs/$slug/ip', permesso: 'ip.read', secondaria: true },
  { sezione: 'jobs', label: 'Attività', icona: <History />, to: '/orgs/$slug/jobs' },
  { sezione: 'members', label: 'Persone', icona: <Group />, to: '/orgs/$slug/members', permesso: ['member.manage', 'member.invite'], secondaria: true },
  { sezione: 'registro', label: 'Registro', icona: <History />, to: '/orgs/$slug/registro', permesso: 'audit.read', secondaria: true },
  { sezione: 'assistenti', label: 'Assistenti', icona: <Assistente />, to: '/orgs/$slug/assistenti', permesso: 'org.read', secondaria: true },
  { sezione: 'impostazioni', label: 'Impostazioni', icona: <Tune />, to: '/orgs/$slug/impostazioni', permesso: ['credential.manage', 'enterprise.manage', 'alert.manage'], secondaria: true },
  { sezione: 'accessi', label: 'Accessi', icona: <Key />, to: '/orgs/$slug/accessi', staff: true, secondaria: true },
];

/**
 * L'impalcatura di ogni schermata autenticata.
 *
 * Il controllo dell'accesso sta qui e non nelle rotte: `/me` è comunque la
 * prima chiamata, e un 401 ha una sola risposta possibile in tutta
 * l'applicazione. Nascondere i controlli che il ruolo non consente è cortesia
 * verso l'utente; la sicurezza resta `Can()` lato hub.
 */
export function Shell() {
  const me = useMe();
  const navigate = useNavigate();
  const path = useRouterState({ select: (s) => s.location.pathname });
  const [altro, setAltro] = useState(false);

  if (me.isPending) {
    // L'avvio a freddo è l'unica volta in cui il marchio si anima per intero:
    // il molo si monta un pezzo per volta mentre si aspetta l'hub.
    return (
      <div className="md-boot">
        <div className="md-boot__molo">
          <Logo size={72} anim="costruzione" label={t('Connessione a Molo')} />
          <Wordmark />
          <p className="md-body-small">{t('Connessione a Molo…')}</p>
        </div>
      </div>
    );
  }
  if (isUnauthenticated(me.error)) {
    return <Login />;
  }
  if (me.error) {
    return <div className="md-boot"><p className="md-body-large">{me.error.message}</p></div>;
  }

  // La lingua della persona, se l'ha scelta, o quella del browser: prima di
  // disegnare qualunque pagina, così nessuna nasce nella lingua sbagliata.
  const scelta: Lingua = me.data?.language ?? linguaDelBrowser();
  if (me.data && scelta !== lingua()) impostaLingua(scelta);

  const orgs = me.data?.organizations ?? [];
  if (orgs.length === 0) {
    return <div className="md-boot"><NoOrganizations email={me.data?.email} /></div>;
  }

  // Lo slug in rotta è la fonte di verità: il selettore lo riflette, non lo
  // decide. Su una rotta senza slug (la radice, mentre reindirizza) si mostra
  // comunque la prima organizzazione invece di una casella vuota.
  const slug = path.match(/^\/orgs\/([^/]+)/)?.[1] ?? orgs[0]!.slug;
  // Le voci che chi guarda può aprire: un viewer non ha Catalogo e Registro.
  const permessi = orgs.find((o) => o.slug === slug)?.permissions;
  const voci = VOCI.filter((v) => (v.staff ? !!me.data?.isSuperadmin : !v.permesso
    || [v.permesso].flat().some((p) => !!permessi?.includes(p))));
  const section: Sezione = path.includes('/backup') ? 'backup'
    : path.includes('/continuita') ? 'continuita'
    : path.includes('/traslochi') ? 'traslochi'
    : path.includes('/catalogo') ? 'catalogo'
    : path.includes('/blueprint') ? 'blueprint'
    : /\/ip(\/|$)/.test(path) ? 'ip'
    : path.includes('/instances') ? 'instances'
    : path.includes('/jobs') ? 'jobs'
    : path.includes('/members') ? 'members'
    : path.includes('/registro') ? 'registro'
    : path.includes('/assistenti') ? 'assistenti'
    : path.includes('/impostazioni') ? 'impostazioni'
    : path.includes('/accessi') ? 'accessi'
    : 'servers';

  return (
    <AppShell
      nav={
        <NavRail
          // Dai 1240dp la rail è una barra laterale: il marchio in cima —
          // fermo, perché le sue animazioni sono attese e qui non si aspetta
          // niente — la tessera con l'azienda che si sta guardando, e in
          // fondo chi sei. Più stretta, questi tre stanno nella barra in
          // alto e nel foglio «Altro»: il CSS li nasconde.
          header={
            <>
              <span className="md-marchio"><Logo size={34} /><Wordmark size={22} /></span>
              <div className="md-tessera-org"><SelettoreOrg slug={slug} orgs={orgs} /></div>
            </>
          }
          footer={
            <>
              <SceltaLingua />
              <Avatar name={me.data?.name || me.data?.email || ''} />
              <span className="md-body-small md-foglio__email">{me.data?.email}</span>
              <BottoneEsci />
            </>
          }
        >
          {voci.map((v) => (
            <NavRailItem
              key={v.sezione}
              icon={v.icona}
              label={t(v.label)}
              secondaria={v.secondaria}
              current={section === v.sezione}
              onClick={() => navigate({ to: v.to, params: { slug } })}
            />
          ))}
          {/* Sul telefono la barra ha quattro voci e questa: le altre, chi
              sei e in quale azienda stai guardando aprono da qui. */}
          <NavRailItem
            className="md-solo-mobile"
            icon={<More />}
            label={t('Altro')}
            current={voci.some((v) => v.secondaria && v.sezione === section)}
            onClick={() => setAltro(true)}
          />
        </NavRail>
      }
    >
      <Dialog open={altro} onOpenChange={setAltro} title={t('Altro')} className="md-dialog--foglio">
        <SelettoreOrg slug={slug} orgs={orgs} />
        <List>
          {voci.filter((v) => v.secondaria).map((v) => (
            <ListItem
              key={v.sezione}
              interactive
              icon={v.icona}
              headline={t(v.label)}
              aria-current={section === v.sezione ? 'page' : undefined}
              onClick={() => {
                setAltro(false);
                navigate({ to: v.to, params: { slug } });
              }}
            />
          ))}
        </List>
        <SceltaLingua />
        <div className="md-foglio__chi">
          <Avatar name={me.data?.name || me.data?.email || ''} />
          <span className="md-body-small md-foglio__email">{me.data?.email}</span>
          <BottoneEsci />
        </div>
      </Dialog>
      <FasciaImpersonazione />
      <Outlet />
    </AppShell>
  );
}

/**
 * Il gruppo a destra della barra: dove sei e chi sei.
 *
 * Sta qui e non nella rail perché la rail è di Molo e non ha uno slot in
 * fondo: aggiungercelo vorrebbe dire cambiare il sistema di design per una
 * scelta di questa applicazione.
 */
export function BarraUtente({ slug }: { slug: string }) {
  const me = useMe();
  const orgs = me.data?.organizations ?? [];
  const email = me.data?.email ?? '';

  return (
    <div className="md-barra-utente">
      <SelettoreOrg slug={slug} orgs={orgs} />
      {/* L'email non si legge ogni volta: sta nel tooltip, e le iniziali
          bastano a sapere con quale account si sta guardando. */}
      <Tooltip content={email} side="bottom">
        <span tabIndex={0} className="md-barra-utente__chi"><Avatar name={me.data?.name || email} /></span>
      </Tooltip>
      <SceltaLingua />
      <BottoneEsci />
    </div>
  );
}

/**
 * Italiano o inglese. La scelta è della persona e la segue su ogni browser;
 * dopo averla salvata la pagina si ricarica, così anche i dati già arrivati
 * dall'hub tornano nella lingua nuova.
 */
function SceltaLingua() {
  const cambia = useMutation({
    mutationFn: (l: Lingua) => mustOk(api.PUT('/api/v1/me/language', { body: { language: l } })),
    onSuccess: (_x, l) => {
      impostaLingua(l);
      location.reload();
    },
  });
  return (
    <SegmentedButton
      className="md-lingua"
      aria-label={t('Lingua del pannello')}
      value={lingua()}
      onValueChange={(v) => {
        if ((v === 'it' || v === 'en') && v !== lingua()) cambia.mutate(v);
      }}
      // Ogni lingua col suo nome, com'è scritto da chi la parla.
      options={[
        { value: 'it', label: 'Italiano' },
        { value: 'en', label: 'English' },
      ]}
    />
  );
}

/** «Esci»: nella barra sul desktop, nel foglio «Altro» sul telefono. */
function BottoneEsci() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const esci = useMutation({
    mutationFn: () => mustOk(api.POST('/api/v1/auth/logout', {})),
    // Si azzera tutto, non solo `me`: la cache contiene server, job e membri
    // dell'organizzazione da cui si sta uscendo, e chi entra dopo sulla stessa
    // macchina non deve vederli comparire per un istante.
    onSuccess: () => {
      qc.clear();
      navigate({ to: '/' });
    },
  });
  return (
    <Button variant="text" icon={esci.isPending ? <Anello /> : undefined} disabled={esci.isPending} onClick={() => esci.mutate()}>
      {esci.isPending ? t('Esco…') : t('Esci')}
    </Button>
  );
}

/** Il valore che nel menu non è un'organizzazione ma l'azione di crearne una. */
const NUOVA = '__nuova';

/**
 * Il selettore delle organizzazioni, e da qui se ne crea una nuova.
 *
 * La creazione sta qui e non in una pagina sua perché è la stessa domanda del
 * selettore — «in quale azienda sto lavorando» — e perché altrimenti non
 * esisterebbe: la schermata `NoOrganizations` la vede solo chi non appartiene
 * a nessuna, quindi chi ne ha già una non aveva alcun modo di aggiungerne
 * un'altra. Con più aziende sullo stesso hub, è la normalità.
 */
function SelettoreOrg({ slug, orgs }: { slug: string; orgs: Array<{ slug: string; name: string }> }) {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [apri, setApri] = useState(false);
  const [nome, setNome] = useState('');

  const crea = useMutation({
    mutationFn: () => must(api.POST('/api/v1/orgs', { body: { slug: nome, name: '' } })),
    onSuccess: (org) => {
      // `me` porta l'elenco delle organizzazioni: senza rileggerlo il
      // selettore non mostrerebbe quella appena creata.
      qc.invalidateQueries({ queryKey: ['me'] });
      setApri(false);
      setNome('');
      navigate({ to: '/orgs/$slug/servers', params: { slug: org.slug } });
    },
  });

  const valido = /^[a-z0-9][a-z0-9-]*$/.test(nome) && nome.length >= 2 && nome.length <= 63;

  return (
    <>
      <Select
        label=""
        aria-label={t('Organizzazione')}
        value={slug}
        options={[
          ...orgs.map((o) => ({ value: o.slug, label: o.name })),
          { value: NUOVA, label: t('+ Nuova organizzazione') },
        ]}
        onChange={(e) => {
          if (e.target.value === NUOVA) {
            setApri(true);
            return;
          }
          navigate({ to: '/orgs/$slug/servers', params: { slug: e.target.value } });
        }}
      />
      <Dialog
        open={apri}
        onOpenChange={(v) => !v && setApri(false)}
        title={t('Nuova organizzazione')}
        description={t("Un'azienda a sé: server, istanze, backup e persone sono suoi e non si mescolano con le altre. Chi la crea ne diventa owner.")}
        actions={
          <>
            <Button variant="text" onClick={() => setApri(false)}>{t('Annulla')}</Button>
            <Button
              variant="filled"
              icon={crea.isPending ? <Anello /> : undefined}
              disabled={!valido || crea.isPending}
              onClick={() => crea.mutate()}
            >
              {crea.isPending ? t('Creo…') : t('Crea')}
            </Button>
          </>
        }
      >
        <form
          className="md-form"
          onSubmit={(e) => {
            e.preventDefault();
            if (valido) crea.mutate();
          }}
        >
          {crea.error && (
            <Banner tone="error" title={t('Non è stata creata')}>{(crea.error as Error).message}</Banner>
          )}
          <TextField
            label={t('Nome breve')}
            value={nome}
            autoFocus
            onChange={(e) => setNome(e.target.value.toLowerCase())}
            error={nome.length > 0 && !valido}
            help={t('Solo minuscole, cifre e trattini, es. «blendx». Compare nei link e non si cambia più.')}
          />
        </form>
      </Dialog>
    </>
  );
}


/**
 * La fascia dell'impersonazione.
 *
 * Chi sta guardando con gli occhi di un altro deve averlo sempre sotto gli
 * occhi. Senza, si torna al pannello dopo un caffè, si legge un dato e si
 * scrive un'email al posto di qualcun altro. Sta in cima a tutto e non si
 * chiude: l'unico modo di toglierla è smettere.
 */
function FasciaImpersonazione() {
  const qc = useQueryClient();
  const me = useMe();
  const imp = me.data?.impersonation;

  const smetti = useMutation({
    mutationFn: () => mustOk(api.DELETE('/api/v1/impersonate')),
    onSuccess: () => qc.invalidateQueries(),
  });

  if (!imp) return null;
  return (
    <div
      className="md-banner"
      style={{
        margin: 'var(--md-sys-spacing-3)',
        background: 'var(--md-sys-color-error-container)',
        color: 'var(--md-sys-color-on-error-container)',
        borderRadius: 'var(--md-sys-shape-corner-medium)',
        border: 'none',
      }}
    >
      <div style={{ flex: 1 }}>
        <div className="md-title-small">{t('Stai guardando come {email}', { email: imp.email })}</div>
        <div className="md-body-small" style={{ marginTop: 2 }}>
          {t('Sola lettura. Finisce da sé alle {ora}.', {
            ora: new Date(imp.until).toLocaleTimeString(localeIntl(), { hour: '2-digit', minute: '2-digit' }),
          })}
        </div>
      </div>
      <Button variant="text" disabled={smetti.isPending} onClick={() => smetti.mutate()}>
        {t('Torna te stesso')}
      </Button>
    </div>
  );
}
